import React, { useState, useEffect } from 'react';

import { object, string } from 'prop-types';
import Countdown from 'react-countdown-now';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { useMembership } from '@techstyle/react-accounts';
import { AssetContainer, HTMLAsset, ImageAsset } from '@techstyle/react-assets';
import { mobile, desktop, useBreakpoint } from '@techstyle/react-components';
import { FormattedMessage } from '@techstyle/react-intl';

import { getQuizDetails } from '../../actions/quiz';
import { parseDate } from '../../utils/dates';
import { getMemberJoinDate } from '../../utils/selectors';
import { Component as AssetCTA } from '../AssetCTA';
import { Container as FullScreenCarousel } from '../FullScreenCarousel';
import TemplateButtons from '../TemplateButtons/TemplateButtons';

/* ======================================== STYLES ======================================== */

const Header = styled.div`
  display: flex;
  padding-bottom: ${props => props.theme.spacing.small}px;
  width: 100%;
  overflow-x: hidden;
`;

const Cell = styled.div`
  align-items: center;
  align-self: center;
  display: flex;
  flex-direction: column;
  flex: 0 0 100%;
  order: initial;
  padding: ${props => (props.padding ? '2em 1em' : 0)};

  text-align: center;

  ${props =>
    props.bgImage &&
    `
    align-items: stretch;
    align-self: stretch;
  `};

  ${desktop`
    flex: 0 0 50%;
    order: ${props => props.order || 'initial'};
  `};
`;

const StyledImageAsset = styled(ImageAsset)`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  flex: 0 0 100%;
  padding: ${props => props.theme.spacing.small}px;

  ${desktop`
    flex: 0 0 50%;
    justify-content: flex-end;
    padding: ${props => props.theme.spacing.large}px;
    order: ${({ cssIsLeft, cssOrder }) =>
      // If the asset has cssIsLeft, its order index will be overridden to
      // display it on the left. Otherwise, it will just be ordered accordingly.
      cssIsLeft ? cssOrder - 1 : cssOrder + 1};
  `};
`;

const TransparentHTMLAsset = styled(HTMLAsset)`
  background-color: transparent;

  h1 {
    font-size: initial;
    line-height: 1.2;
    font-weight: normal;
  }
`;

const StyledAssetCTA = styled(AssetCTA)`
  margin-top: ${props => props.theme.spacing.moderate}px;
`;

const StyledSecondaryImage = styled(ImageAsset)`
  max-width: 100%;
`;

const StyledFullScreenCarousel = styled(FullScreenCarousel)`
  height: 233px;
  .slick-arrow {
    bottom: 27%;
  }

  ${mobile`
    width: 100%;

    .slick-dots {
      bottom: 0px;
      line-height: 40px;
    };

    ${FullScreenCarousel.StyledHTMLAsset} {
      text-align: center;
    }
  `};

  ${desktop`
    width: 50%;
    height: 435px;

    .slick-arrow {
      bottom: 15%;
    }
  `};
`;
/* ======================================== END of STYLES ======================================== */

export default function QuizResultsHeaderAsset({
  asset,
  className,
  containerName,
}) {
  const { isMobile } = useBreakpoint();

  const memberJoinDate = useSelector(getMemberJoinDate);
  const pageRenderDate = useSelector(state =>
    parseDate(state.time.pageRenderDate).getTime()
  );
  const oneHour = 60 * 60 * 1000;
  const endDate =
    (memberJoinDate ? memberJoinDate.getTime() : pageRenderDate) + oneHour;

  const dispatch = useDispatch();
  const { customerId } = useMembership();
  const [quizData, setQuizData] = useState(null);
  const quizDetails = quizData?.payload || [];

  useEffect(() => {
    if (!customerId) {
      return;
    }

    async function getQuizData() {
      const data = await dispatch(getQuizDetails(customerId));
      setQuizData(data);
    }

    getQuizData();
  }, [customerId, dispatch]);

  /**
   * This filters the assets, segregating them
   * according to the user's interests.
   */
  const getAssetsForPostReg = assets => {
    const userInterestedCategories = quizDetails
      ?.filter(
        detail =>
          detail?.name.match(/is_interested_in_(.*)/) && detail?.value === 'YES'
      )
      ?.map(category => category.name);

    /**
     * If the user has interests, we collect the assets that matches with
     * the user's interests and those with no quizCategories.
     *
     * If the user has no interests, we only collect the assets with no quizCategories.
     *
     * (Now, in the above scenarios, if the user has no interests, no matching assets,
     * or no empty quizCategories, we return an empty list. This will display nothing.)
     */
    const assetsList = [];
    const userHasInterests = Boolean(userInterestedCategories?.length);

    assets.map(asset => {
      const { quizCategory: quizCategories } = asset.options.customVars;
      const assetHasNoQuizCategory = !quizCategories;

      if (userHasInterests) {
        const assetCategories = quizCategories?.split(',');
        const isAssetFoundInUserInterests = assetCategories?.some(category =>
          userInterestedCategories?.includes(category.trim())
        );

        if (assetHasNoQuizCategory || isAssetFoundInUserInterests) {
          assetsList.push(asset);
        }
      } else if (assetHasNoQuizCategory) {
        assetsList.push(asset);
      }
    });

    return assetsList;
  };

  const renderSecondaryItems = () => {
    /**
     * The hover image holds the speed dial image and hover text should appear after it
     */
    const hasSecondaryImage = asset.options.hoverImage;

    if (!hasSecondaryImage) {
      return null;
    }

    return (
      <>
        <StyledSecondaryImage
          asset={asset}
          imageFilename={asset.options.hoverImage}
        />
        <HTMLAsset
          asset={{
            ...asset,
            options: {
              ...asset.options,
              htmlText: asset.options.hoverText,
            },
          }}
        />
      </>
    );
  };

  const renderText = order => {
    return (
      <Cell padding order={order}>
        <HTMLAsset asset={asset} />
        {renderSecondaryItems()}
        <TemplateButtons asset={asset} />
        <Countdown date={endDate} />
      </Cell>
    );
  };

  if (asset) {
    const {
      options: {
        customVars: { imagePosition },
      },
    } = asset;
    return (
      <>
        <Cell order={imagePosition === 'left' ? 0 : 1}>
          <StyledImageAsset asset={asset} />
        </Cell>
        {renderText(imagePosition === 'left' ? 1 : 0)}
      </>
    );
  }

  return (
    <AssetContainer className={className} name={containerName} multi>
      {assets => {
        if (!assets || !assets.data) {
          return null;
        }

        const assetsToUse = getAssetsForPostReg(assets.data);

        const combinedAssets = assetsToUse.filter(
          asset => asset.options.customVars.isCarousel
        );

        const noCarouselAssetsList = assetsToUse.filter(
          asset => !asset.options.customVars.isCarousel
        );

        const noCarouselAssets = noCarouselAssetsList.map((asset, key) => {
          const isLeft =
            asset.options.customVars.imagePosition?.toLowerCase()?.trim() ===
            'left';

          return (
            <StyledImageAsset
              asset={asset}
              background
              cssIsLeft={isLeft}
              cssOrder={key}
              key={key}
            >
              <TransparentHTMLAsset asset={asset} />
              <StyledAssetCTA
                {...asset}
                defaultText={
                  <FormattedMessage
                    defaultMessage="Get Started"
                    id="site_account_rewards.get_started"
                  />
                }
              />
            </StyledImageAsset>
          );
        });

        const noCarouselAssetsMobile = noCarouselAssets.filter(
          (asset, idx) =>
            !noCarouselAssetsList[idx].options.customVars.shouldHideOnMobile
        );

        return (
          <Header>
            <StyledFullScreenCarousel
              combinedAssets={combinedAssets}
              countDownEndDate={endDate}
              needMobileArrows
              videoHeight={isMobile ? '97%' : false}
            />
            {isMobile ? noCarouselAssetsMobile : noCarouselAssets}
          </Header>
        );
      }}
    </AssetContainer>
  );
}

QuizResultsHeaderAsset.propTypes = {
  asset: object,
  className: string,
  containerName: string,
};
